import { HttpInterceptorFn, HttpHandlerFn, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { inject } from '@angular/core';
import { ToastService } from '../services/toast.service';

/**
 * List of endpoints to exclude from triggering HTTP error handling.
 */
const excludedEndpoints: string[] = ['login', 'country', 'lockers/events', 'check-cookie'];

/**
 * Checks if the current request URL matches any of the excluded endpoints.
 */
const isExcludedEndpoint = (url: string): boolean => {
  return excludedEndpoints.some(endpoint => url.includes(endpoint));
};

/**
 * Handles the error response by showing an appropriate toast message.
 */
const handleHttpError = (error: HttpErrorResponse, toastService: ToastService): Observable<never> => {
  let message = '';
  let title = 'Error';

  if (error.error instanceof ErrorEvent) {
    // Client-side error
    message = `Client Error: ${error.error.message}`;
    title = 'Client Error';
  } else {
    // Server-side error
    message = `Error: ${error?.error?.error ?? error?.message}`;
    title = 'Server Error';
  }

  // Show error toast and log the error
  toastService.showError({ message, title });
  console.error('HTTP Error:', error);

  return throwError(() => new Error(message));
};

/**
 * HTTP interceptor function to handle errors globally.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const toastService = inject(ToastService);

  if (isExcludedEndpoint(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => handleHttpError(error, toastService))
  );
};